import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { FaCheckCircle, FaExclamationTriangle, FaRedo, FaTrash } from 'react-icons/fa';
import { CgSpinner } from 'react-icons/cg';
import { TbCloudDownload, TbLink, TbLinkOff, TbChevronLeft } from 'react-icons/tb';
import { Link, useParams, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';

import PageHeader from '../../components/misc/PageHeader';
import SearchBar from '../../components/forms/SearchBar';
import Labeled from '../../components/misc/Labeled';
import useSelectedCommunity from '../../../hooks/community/useSelectedCommunity';
import {
  FinancialTable,
  FinancialDataCell,
} from '../../components/tables/FinancialTable';
import EmptyScreenView from '../../components/misc/EmptyScreenView';
import * as bankAccountService from '../../../services/community/bankAccountService';

import {
  BankAccount,
  BankMutation,
  JournalEntryModel,
  CreateJournalEntryDto,
  CreateBankTransactionModel,
  GoCardlessInstitution,
  TransactionType,
} from '../../../types/bankAccount';
import ModalLayout from '../../components/layouts/ModalLayout';
import Button from '../../components/buttons/Button';
import { formatDate } from '../../../utils/stringUtils';
import AssignSplitTransactionModal from '../../components/modals/AssignSplitTransactionModal';
import TransactionModal, { SplitRow } from '../../components/modals/TransactionModal';
import DeclarationModal from '../../components/modals/DeclarationModal';
import {
  formatEuroCents,
  formatEuroCentsWithSign,
  matchMutationsToConnections,
  calculateNetAmount,
} from '../../../utils/bankAccountUtil';
import DropdownSelect from '../../components/forms/DropdownSelect';
import MutationIntoTransactionsModal from '../../components/modals/MutationIntoTransactionsModal';
import WithTooltip from '../../components/misc/WithTooltip';
import DatePicker from '../../components/forms/DatePicker';
import { RootState } from '../../../redux/reducers';
import { Connection, ConnectionType } from '../../../redux/slices/connectionsSlice';
import { Event, EventParticipationAnswer } from '../../../types/event';

import VerifyTransactionMatchesView from '../../components/financial/VerifyTransactionMatchesView';
import VerifyContributionPlanMatchesView from '../../components/financial/VerifyContributionPlanMatchesView';
import VerifyEventExpenseMatchesView from '../../components/financial/VerifyEventExpenseMatchesView';
import VerifyInternalMutationMatchesView from '../../components/financial/VerifyInternalMutationMatchesView';
import HandleUnmatchedTransactionsView from '../../components/financial/HandleUnmatchedTransactionsView';

interface MutationsPageProps {
  hideHeader?: boolean;
}

export default function MutationsPage({ hideHeader = false }: MutationsPageProps): JSX.Element {
  const { t, i18n } = useTranslation('', { keyPrefix: 'page.financial.accountEntry' });
  const params = useParams();
  const navigate = useNavigate();
  const dashboardId = useSelector((state: RootState) => state.application.selectedDashboard);
  const [search, setSearch] = useState<string>('');
  const [bankAccount, setBankAccount] = useState<BankAccount | null>();
  const [allBankAccounts, setAllBankAccounts] = useState<BankAccount[]>();
  const [mutations, setMutations] = useState<BankMutation[] | null | undefined>(null);

  // EXCEL UPLOAD
  const [importModalOpen, setImportModalOpen] = useState<boolean>(false);
  const [transactionModalOpen, setTransactionModalOpen] = useState<boolean>(false);
  const [selectedMutation, setSelectedMutation] = useState<BankMutation | null>(null);
  const [splitModalMutationOpen, setSplitModalMutationOpen] = useState<boolean>(false);
  const [splitModalTransactionsOpen, setSplitModalTransactionsOpen] = useState<boolean>(false);
  const [cardlessModalOpen, setCardlessModalOpen] = useState<boolean>(false);
  const [splitModalMutation, setSplitModalMutation] = useState<BankMutation | null>(null);
  const [splitModalJournalEntries, setSplitModalJournalEntries] = useState<
    CreateJournalEntryDto[] | null
  >(null);
  const [splitModalTotalCount, setSplitModalTotalCount] = useState<number>(0);
  const [splitModalCreatedIds, setSplitModalCreatedIds] = useState<
    { journalEntryId: number; mutationId: number }[]
  >([]);
  const [filter, setFilter] = useState<'ALL' | 'PROCESSED' | 'UNPROCESSED'>('ALL');
  const [fetchingLoading, setFetchingLoading] = useState<boolean>(false);
  const [tmInitialProcessType] = useState<'ASSIGN' | 'SPLIT' | 'INTERNAL'>();
  const [tmInitialInternalSide] = useState<'FROM' | 'TO'>();

  // When opening a processed mutation in edit mode, store the existing journal entry(ies)
  const [selectedExistingJournalEntry, setSelectedExistingJournalEntry] =
    useState<JournalEntryModel | null>(null);
  /** All journal entries for the selected mutation (siblings when editing a split) */
  const [selectedExistingJournalEntries, setSelectedExistingJournalEntries] = useState<
    JournalEntryModel[] | null
  >(null);

  const community = useSelectedCommunity();

  // FIXME
  // const { items: transactionsPerPage, ...pagination } = usePagination(bankAccount?.transactions ?? []);

  useEffect(() => {
    bankAccountService.getAllBankAccounts().then((bs) => {
      setAllBankAccounts(bs);

      // If we have a specific bank account ID from the URL, use that
      if (params.bankAccountId) {
        const specificAccount = bs.find(
          (account) => account.accountId.toString() === params.bankAccountId,
        );
        if (specificAccount) {
          setBankAccount(specificAccount);
        }
      } else if (bs.length > 0) {
        // Default behavior: use first account
        setBankAccount(bs[0]);
      }
    });
  }, [community, params.bankAccountId]);

  useEffect(() => {
    if (!bankAccount) {
      return;
    }

    setMutations(undefined);

    bankAccountService.getAllMutationsForBank(bankAccount.accountId).then(setMutations);
  }, [bankAccount]);

  useEffect(() => {
    if (!splitModalTransactionsOpen) {
      setSplitModalJournalEntries(null);
      setSplitModalTotalCount(0);
      setSplitModalCreatedIds([]);
    }
  }, [splitModalTransactionsOpen]);

  useEffect(() => {
    if (!transactionModalOpen) {
      setSelectedMutation(null);
    }
  }, [transactionModalOpen]);

  const createBankAccountJournalEntries = (
    bulkConfirmedMatches: {
      transaction: CreateBankTransactionModel;
      matchedConnection: Connection;
      contributionPlanId?: number;
    }[],
    selectedGroupMutations: CreateBankTransactionModel[],
  ) => {
    if (!dashboardId) {
      toast.error('No dashboard selected');
      return;
    }

    // Note: The bulk modal still uses CreateBankTransactionModel, so we need to convert them
    // TODO: Refactor bulk modal to use journal entries directly
    // For now, we'll create journal entries via the transaction service which handles conversion
    // Most journal entries are created automatically via posting rules
    const allTransactions = [
      ...bulkConfirmedMatches.map(m => m.transaction),
      ...selectedGroupMutations,
    ];

    // Use the transaction service which will create journal entries via posting rules
    bankAccountService.createTransactionsWithParticipations(allTransactions).then(() => {
      setMutations((prevMutations) => {
        if (!prevMutations) return prevMutations;
        // For each transaction created, update the corresponding mutation
        let updatedMutations = [...prevMutations];
        allTransactions.forEach((txn) => {
          updatedMutations = updatedMutations.map((m) =>
            m.mutationId === txn.mutationId
              ? {
                ...m,
                journalEntryCount: m.journalEntryCount + 1,
              }
              : m,
          );
        });
        return updatedMutations;
      });
      toast.success(t('success.importBankTransactions'));
      setImportModalOpen(false);
    }).catch((error) => {
      console.error('Error creating journal entries:', error);
      toast.error('Failed to create journal entries');
    });
  };

  const handleCreateGoCardlessLink = async (
    bankId: string,
    fromDate?: string,
  ) => {
    if (!bankAccount || !bankAccount?.accountId) {
      toast.error(t('error.createGoCardlessLink'));
      return;
    }

    await bankAccountService
      .createGoCardlessLink(
        bankAccount.accountId,
        bankId,
        i18n.language as 'en' | 'nl',
      )
      .then((linkData) => {
        if (fromDate) {
          localStorage.setItem(`gc_fromDate_${bankAccount.accountId}`, fromDate);
        }
        const dummyAnchor = document.createElement('a');
        dummyAnchor.href = linkData.link;
        dummyAnchor.click();
        dummyAnchor.remove();
      })
      .catch((err) => {
        console.error('Error creating GoCardless link:', err);
        toast.error(t('error.createGoCardlessLink'));
      });
  };

  const isGcReauthRequired = (account: BankAccount | null | undefined) => {
    if (!account) return false;
    if (account.autoFetchDisabledReason === 'GC_ACCESS_EXPIRED') {
      return true;
    }

    if (account.autoFetchDisabledReason === 'USER_DISABLED') {
      return false;
    }

    return account.autoFetchEnabled === false && !!account.goCardlessAccountId;
  };

  const handleReconnectGoCardless = async () => {
    if (!bankAccount || !bankAccount.accountId) {
      toast.error(t('error.createGoCardlessLink'));
      return;
    }
    if (!bankAccount.requisitionId) {
      toast.error(t('error.createGoCardlessLink'));
      return;
    }

    try {
      const gcAccounts = await bankAccountService.getAccountsByRequisition(
        bankAccount.requisitionId,
      );
      const gcAccount =
        (bankAccount.goCardlessAccountId &&
          gcAccounts.find((a) => a.id === bankAccount.goCardlessAccountId)) ||
        gcAccounts[0];
      if (!gcAccount) {
        throw new Error('NO_GC_ACCOUNT');
      }

      const linkData = await bankAccountService.createGoCardlessLink(
        bankAccount.accountId,
        gcAccount.institution_id,
        i18n.language as 'en' | 'nl',
      );
      const dummyAnchor = document.createElement('a');
      dummyAnchor.href = linkData.link;
      dummyAnchor.click();
      dummyAnchor.remove();
    } catch (err) {
      console.error('Error creating GoCardless link for reauth:', err);
      toast.error(t('error.createGoCardlessLink'));
    }
  };

  const unlinkAccount = async () => {
    if (!bankAccount || !bankAccount?.accountId) {
      toast.error(t('error.unlinkGoCardlessAccount'));
      return;
    }

    bankAccountService
      .unlinkAccountFromGoCardless(bankAccount.accountId)
      .then(() => {
        toast.success(t('success.unlinkGoCardlessAccount'));
        setBankAccount({ ...bankAccount!, requisitionId: undefined });
      })
      .catch(() => {
        toast.error(t('error.unlinkGoCardlessAccount'));
      });
  };

  const refetchMutations = async () => {
    if (!bankAccount || !bankAccount.accountId || !bankAccount.goCardlessAccountId) {
      toast.error(t('error.refetchMutations'));
      return;
    }

    setFetchingLoading(true);

    bankAccountService
      .fetchLatestGoCardlessTransactions(bankAccount.accountId)
      .then(() => {
        setBankAccount((prev) =>
          prev
            ? {
              ...prev,
              latestFetching: new Date(),
              gocardlessManualFetchCount: (prev.gocardlessManualFetchCount ?? 0) + 1,
            }
            : prev,
        );

        return bankAccountService.getAllMutationsForBank(bankAccount.accountId).then(setMutations);
      })
      .then(() => toast.success(t('success.refetchMutations')))
      .catch(() => toast.error(t('error.refetchMutations')))
      .finally(() => setFetchingLoading(false));
  };

  const deleteBankAccount = async () => {
    if (!bankAccount || !bankAccount?.accountId) {
      toast.error('No bank account selected');
      return;
    }

    if (
      window.confirm(
        'Are you sure you want to delete this bank account? This action cannot be undone.',
      )
    ) {
      try {
        await bankAccountService.deleteBankAccount(bankAccount.accountId);
        toast.success('Bank account deleted successfully');
        // Navigate back to settings since the account is deleted
        navigate('/financial/overview');
      } catch (error) {
        toast.error('Failed to delete bank account');
      }
    }
  };

  return (
    <>
      {!hideHeader ? (
        <>
          {params.bankAccountId && (
            <Link to="/financial/overview">
              <Button className="mb-2 gap-1 p-0">
                <TbChevronLeft size={20} /> Back
              </Button>
            </Link>
          )}
          <PageHeader
            title={t('title')}
            badge={
              bankAccount ? (
                <div className="rounded-full bg-primary-300 px-4 py-2">
                  <p className="text-sm font-medium">
                    {bankAccount.iban} ({formatEuroCents(bankAccount.balance, i18n.language)})
                  </p>
                </div>
              ) : undefined
            }
            subtitle={t('subtitle')}
          />
          {params.bankAccountId && (
            <div className="mb-4 flex items-center justify-end">
              <div className="flex items-center gap-2">
                {bankAccount?.requisitionId ? (
                  bankAccount?.transferCompleted ? (
                    <>
                      {isGcReauthRequired(bankAccount) ? (
                        <Button variant="primary-dark" onClick={handleReconnectGoCardless}>
                          <TbLink />
                          {t('reauthButton', { defaultValue: 'Reconnect bank' })}
                        </Button>
                      ) : (
                        <WithTooltip
                          className="w-72 whitespace-normal text-center"
                          tooltip={t('refetchTooltip')}>
                          <Button
                            variant="secondary"
                            disabled={
                              fetchingLoading ||
                          (typeof bankAccount.gocardlessManualFetchCount === 'number' &&
                            bankAccount.gocardlessManualFetchCount >= 2)
                            }
                            onClick={() => refetchMutations()}>
                            {fetchingLoading ? (
                              <CgSpinner className="animate-spin" />
                            ) : (
                              <TbCloudDownload />
                            )}
                            {t('refetchButton')}
                          </Button>
                        </WithTooltip>
                      )}
                      <Button variant="danger-v3" onClick={() => unlinkAccount()}>
                        <TbLinkOff />
                        {t('unlinkButton')}
                      </Button>
                      <Button variant="danger-v3" onClick={() => deleteBankAccount()}>
                        <FaTrash />
                        Delete
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button onClick={() => unlinkAccount()} variant="primary-dark">
                        <FaRedo />
                        {t('setupFailedButton')}
                      </Button>
                      <Button variant="danger-v3" onClick={() => deleteBankAccount()}>
                        <FaTrash />
                        Delete
                      </Button>
                    </>
                  )
                ) : (
                  <>
                    <Button variant="primary-dark" onClick={() => setCardlessModalOpen(true)}>
                      <TbLink />
                      {t('linkButton')}
                    </Button>
                    <Button variant="danger-v3" onClick={() => deleteBankAccount()}>
                      <FaTrash />
                      Delete
                    </Button>
                  </>
                )}
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="mb-4 flex items-center justify-between">
          {params.bankAccountId ? (
            // Show back button when viewing specific bank account
            <Link to="/financial/overview">
              <Button className="gap-1 p-0">
                <TbChevronLeft size={20} /> Back
              </Button>
            </Link>
          ) : (
            // Show dropdown when viewing all accounts
            <div className="flex items-center gap-2">
              <p className="font-medium">Select bank account:</p>
              <DropdownSelect
                options={(allBankAccounts ?? []).map((v) => ({ value: v, label: v.iban }))}
                setValue={setBankAccount}
                value={bankAccount}
                className="my-2 w-72"
              />
            </div>
          )}
          <div className="flex items-center gap-2">
            {bankAccount?.requisitionId ? (
              bankAccount?.transferCompleted ? (
                <>
                  {isGcReauthRequired(bankAccount) ? (
                    <Button variant="primary-dark" onClick={handleReconnectGoCardless}>
                      <TbLink />
                      {t('reauthButton', { defaultValue: 'Reconnect bank' })}
                    </Button>
                  ) : (
                    <WithTooltip
                      className="w-72 whitespace-normal text-center"
                      tooltip={t('refetchTooltip')}>
                      <Button
                        variant="secondary"
                        disabled={
                          fetchingLoading ||
                          (typeof bankAccount.gocardlessManualFetchCount === 'number' &&
                            bankAccount.gocardlessManualFetchCount >= 2)
                        }
                        onClick={() => refetchMutations()}>
                        {fetchingLoading ? (
                          <CgSpinner className="animate-spin" />
                        ) : (
                          <TbCloudDownload />
                        )}
                        {t('refetchButton')}
                      </Button>
                    </WithTooltip>
                  )}
                  <Button variant="danger-v3" onClick={() => unlinkAccount()}>
                    <TbLinkOff />
                    {t('unlinkButton')}
                  </Button>
                  <Button variant="danger-v3" onClick={() => deleteBankAccount()}>
                    <FaTrash />
                    Delete
                  </Button>
                </>
              ) : (
                <>
                  <Button onClick={() => unlinkAccount()} variant="primary-dark">
                    <FaRedo />
                    {t('setupFailedButton')}
                  </Button>
                  <Button variant="danger-v3" onClick={() => deleteBankAccount()}>
                    <FaTrash />
                    Delete
                  </Button>
                </>
              )
            ) : (
              <>
                <Button variant="primary-dark" onClick={() => setCardlessModalOpen(true)}>
                  <TbLink />
                  {t('linkButton')}
                </Button>
                <Button variant="danger-v3" onClick={() => deleteBankAccount()}>
                  <FaTrash />
                  Delete
                </Button>
              </>
            )}
          </div>
        </div>
      )}

      <div className="flex flex-1 flex-col gap-5 pb-4">
        {mutations && (
          <>
            {mutations && false && (
              <div className="flex flex-col gap-4 rounded-xl bg-secondary p-4">
                <div>
                  <h3 className="text-lg font-medium">Warning!</h3>
                  <p>
                    {t('warningMessageSplit', {
                      count: 0, // mutations.filter((m) => !m).length,
                    })}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Link to="/financial/bulk-split">
                    <Button variant="primary-dark" className="text-sm">
                      {t('bulkSplit')}
                    </Button>
                  </Link>
                  <p>{t('manuallySplit')}</p>
                </div>
              </div>
            )}
            <div className="flex items-center justify-between">
              <SearchBar className="max-w-[380px]" search={search} setSearch={setSearch} />
            </div>
          </>
        )}
        {mutations === undefined ? (
          <EmptyScreenView title={t('loading')} subtitle="" />
        ) : mutations === null ? (
          <section className="mt-8 flex w-full flex-col items-center justify-center gap-4">
            <img
              src="/assets/figures/phone_hi.svg"
              alt="vera mess"
              className="w-1/4 max-md:w-1/2"
            />
            <h2 className="mt-2 text-center text-2xl">{t('selectBankAccountOrCreateOne')}</h2>
          </section>
        ) : (
          <>
            {mutations.length === 0 ? (
              <EmptyScreenView />
            ) : (
              <>
                <div className="flex items-center gap-2">
                  <p>{t('filter')}</p>
                  <Button
                    onClick={() =>
                      setFilter((prev) => (prev === 'PROCESSED' ? 'ALL' : 'PROCESSED'))
                    }
                    className={`${filter === 'PROCESSED' ? 'bg-success' : 'bg-secondary-200'} text-sm text-white`}>
                    {t('processed')}
                  </Button>
                  <Button
                    onClick={() =>
                      setFilter((prev) => (prev === 'UNPROCESSED' ? 'ALL' : 'UNPROCESSED'))
                    }
                    className={`${filter === 'UNPROCESSED' ? 'bg-orange-500' : 'bg-secondary-200'} text-sm text-white`}>
                    {t('unprocessed')}
                  </Button>
                </div>
                <div className="flex flex-col gap-8">
                  <FinancialTable
                    columns={[
                      { id: 'date', label: t('table.date') },
                      { id: 'description', label: t('table.description') },
                      { id: 'amount', label: t('table.amount') },
                      { id: 'balanceAfter', label: t('table.balanceAfter') },
                      { id: 'transactions', label: t('table.transactions') },
                      { id: 'status', label: t('table.status') },
                    ]}
                    rows={mutations.filter((tns) => {
                      // First apply the filter (ALL, PROCESSED, UNPROCESSED)
                      if (filter === 'ALL') {
                        // For ALL, we still need to check if it matches the search
                      } else if (filter === 'PROCESSED') {
                        if (tns.journalEntryCount === 0) return false;
                      } else if (filter === 'UNPROCESSED') {
                        if (tns.journalEntryCount > 0) return false;
                      }

                      // Then apply search filtering
                      if (search.trim()) {
                        const searchLower = search.toLowerCase();
                        return (
                          tns.oppositeName?.toLowerCase().includes(searchLower) ||
                          tns.description?.toLowerCase().includes(searchLower)
                        );
                      }

                      return true;
                    })}
                    renderRow={(m) => {
                      const handleRowClick = async () => {
                        setSelectedMutation(m);
                        // If processed, fetch existing journal entry(ies) to open in edit mode
                        if (m.journalEntryCount > 0) {
                          try {
                            const allJournalEntries =
                              await bankAccountService.getAllJournalEntries();
                            const siblings = allJournalEntries.filter(
                              (entry) => entry.mutationId === m.mutationId,
                            );
                            setSelectedExistingJournalEntry(siblings[0] ?? null);
                            setSelectedExistingJournalEntries(siblings.length > 0 ? siblings : null);
                          } catch (e) {
                            setSelectedExistingJournalEntry(null);
                            setSelectedExistingJournalEntries(null);
                          }
                        } else {
                          setSelectedExistingJournalEntry(null);
                          setSelectedExistingJournalEntries(null);
                        }
                        setTransactionModalOpen(true);
                      };

                      return (
                        <>
                          <FinancialDataCell onClick={handleRowClick}>
                            {formatDate(new Date(m.transactionDate))}
                          </FinancialDataCell>
                          <FinancialDataCell onClick={handleRowClick}>
                            <p className="text-sm">{m.oppositeName}</p>
                            <p className="text-xs text-gray-400">
                              {m.description.slice(0, 50)}
                              {m.description.length >= 50 && '...'}
                            </p>
                          </FinancialDataCell>
                          <FinancialDataCell onClick={handleRowClick}>
                            {formatEuroCentsWithSign(m.amount, i18n.language)}
                          </FinancialDataCell>
                          <FinancialDataCell onClick={handleRowClick}>
                            {formatEuroCentsWithSign(m.balanceAfter, i18n.language, false)}
                          </FinancialDataCell>
                          <FinancialDataCell onClick={handleRowClick}>
                            {m.journalEntryCount > 0 ? (
                              <span className="text-sm font-medium text-gray-700">
                                {m.journalEntryCount}
                              </span>
                            ) : (
                              <span className="text-sm text-gray-400">-</span>
                            )}
                          </FinancialDataCell>
                          <FinancialDataCell onClick={handleRowClick}>
                            <div className="flex items-center gap-2">
                              {m.journalEntryCount === 0 ? (
                                <div className="flex w-fit items-center gap-2 rounded-full bg-orange-500 px-3 py-1 text-white">
                                  <FaExclamationTriangle /> {t('unprocessed')}
                                </div>
                              ) : (
                                <div className="flex w-fit items-center gap-2 rounded-full bg-success px-3 py-1 text-white">
                                  <FaCheckCircle /> {t('processed')}
                                </div>
                              )}
                            </div>
                          </FinancialDataCell>
                        </>
                      );
                    }}
                  />
                </div>
              </>
            )}
          </>
        )}

        {bankAccount && (
          <AssignBulkMutationModal
            unassignedMutations={mutations?.filter((m) => m.journalEntryCount === 0) ?? []}
            open={importModalOpen}
            setOpen={setImportModalOpen}
            onParseSuccess={createBankAccountJournalEntries}
            // setParentConfirmedMatches is required by the modal props but not used here
            setParentConfirmedMatches={() => undefined}
            onUpdateMutations={setMutations}
          />
        )}

        {bankAccount && (
          <LinkBankAccountModal
            open={cardlessModalOpen}
            setOpen={setCardlessModalOpen}
            onBankChosen={handleCreateGoCardlessLink}
          />
        )}

        {selectedMutation && selectedExistingJournalEntry && selectedExistingJournalEntry.payerId && (
          <DeclarationModal
            open={transactionModalOpen}
            setOpen={setTransactionModalOpen}
            initialTransaction={(() => {
              // In declaration edit mode, convert existing journal entry back to a legacy transaction model
              const entry = selectedExistingJournalEntry;
              const amount = calculateNetAmount(entry);

              // Map journal entry source type to legacy transaction type
              let transactionType: TransactionType;
              switch (entry.sourceType) {
                case 'PERSONAL_EXPENSE':
                  transactionType = TransactionType.PERSONAL_EXPENSE;
                  break;
                case 'PERSONAL_INCOME':
                  transactionType = TransactionType.PERSONAL_INCOME;
                  break;
                case 'GROUP_INCOME':
                  transactionType = TransactionType.GROUP_INCOME;
                  break;
                case 'CONTRIBUTION_RUN':
                  transactionType = TransactionType.CONTRIBUTION;
                  break;
                case 'GROUP_EXPENSE':
                default:
                  transactionType = TransactionType.GROUP_EXPENSE;
                  break;
              }

              const legacyModel: CreateBankTransactionModel = {
                amount,
                description: entry.description,
                date: new Date(entry.entryDate),
                mutationId: entry.mutationId,
                payerId: entry.payerId,
                transactionType,
                // For now we omit participations; DeclarationModal will let the user (re)assign splits
              };

              return legacyModel;
            })()}
            onSplitSuccess={(tns: CreateBankTransactionModel | CreateBankTransactionModel[]) => {
              if (Array.isArray(tns)) {
                toast.error('Splitting declarations in edit mode is not supported yet');
                setTransactionModalOpen(false);
                setSelectedExistingJournalEntry(null);
                setSelectedExistingJournalEntries(null);
                setSelectedMutation(null);
                return;
              }

              // Note: DeclarationModal still returns CreateBankTransactionModel
              // TODO: Update DeclarationModal to work with journal entries
              // For now, use the deprecated update method
              bankAccountService
                .updateBankTransaction(selectedExistingJournalEntry!.journalEntryId, tns)
                .then(() => {
                  setTransactionModalOpen(false);
                  setSelectedExistingJournalEntry(null);
                  setSelectedExistingJournalEntries(null);
                  setSelectedMutation(null);
                  toast.success('Journal entry updated');
                })
                .catch(() => toast.error('Failed to update journal entry'));
            }}
          />
        )}

        {selectedMutation &&
          (!selectedExistingJournalEntry || !selectedExistingJournalEntry.payerId) && (
            <TransactionModal
              open={transactionModalOpen}
              setOpen={setTransactionModalOpen}
              initialJournalEntry={selectedExistingJournalEntry ?? undefined}
              initialSplitJournalEntries={
                selectedExistingJournalEntries && selectedExistingJournalEntries.length > 1
                  ? selectedExistingJournalEntries
                  : undefined
              }
              initialSplitTargetAmount={
                selectedExistingJournalEntries && selectedExistingJournalEntries.length > 1
                  ? selectedMutation.amount
                  : undefined
              }
              mutation={selectedMutation}
              initialProcessType={tmInitialProcessType}
              initialInternalSide={tmInitialInternalSide}
              onInternalCreate={({ fromMutationId, toMutationId, description }) => {
                bankAccountService
                  .createInternalTransferFromMutations(fromMutationId, toMutationId, description)
                  .then(() => {
                    setMutations((prev) => {
                      if (!prev) return prev;
                      // Mark both source mutations as processed
                      return prev.map((m) =>
                        m.mutationId === fromMutationId || m.mutationId === toMutationId
                          ? { ...m, journalEntryCount: m.journalEntryCount + 1 }
                          : m,
                      );
                    });
                    setTransactionModalOpen(false);
                    setSelectedMutation(null);
                    toast.success('Internal transfer created');
                  })
                  .catch(() => toast.error('Failed to create internal transfer'));
              }}
              // onOpenCounterpart removed per request
              onSplitSuccess={(journalEntryOrArray: CreateJournalEntryDto | SplitRow[]) => {
                if (Array.isArray(journalEntryOrArray)) {
                  const mutationId = selectedMutation?.mutationId;

                  // Edit mode: existing journal entry(ies) being split or edited
                  if (selectedExistingJournalEntry) {
                    const isEditingExistingSplit =
                      selectedExistingJournalEntries &&
                      selectedExistingJournalEntries.length > 1;
                    const allHaveIds =
                      journalEntryOrArray.length > 0 &&
                      journalEntryOrArray.every(
                        (r): r is SplitRow & { journalEntryId: number } =>
                          typeof (r as SplitRow).journalEntryId === 'number',
                      );
                    const sameCount =
                      isEditingExistingSplit &&
                      journalEntryOrArray.length === selectedExistingJournalEntries!.length;

                    if (isEditingExistingSplit && sameCount && allHaveIds) {
                      // Update each existing split row
                      Promise.all(
                        journalEntryOrArray.map((entryRow) =>
                          bankAccountService.updateJournalEntry(
                            (entryRow as SplitRow & { journalEntryId: number }).journalEntryId!,
                            entryRow as CreateJournalEntryDto,
                          ),
                        ),
                      )
                        .then(() => {
                          setTransactionModalOpen(false);
                          setSelectedExistingJournalEntry(null);
                          setSelectedExistingJournalEntries(null);
                          setSelectedMutation(null);
                          toast.success('Journal entry updated');
                        })
                        .catch(() => toast.error('Failed to update journal entry'));
                      return;
                    }

                    // Single → multiple or different count: delete existing, create new
                    const toDelete = isEditingExistingSplit
                      ? selectedExistingJournalEntries!
                      : [selectedExistingJournalEntry];
                    Promise.all(
                      toDelete.map((journalEntry) =>
                        bankAccountService.deleteJournalEntry(journalEntry.journalEntryId),
                      ),
                    )
                      .then(() =>
                        Promise.all(
                          journalEntryOrArray.map((row) =>
                            bankAccountService.createJournalEntry({
                              ...(row as CreateJournalEntryDto),
                              dashboardId: dashboardId!,
                              mutationId: mutationId ?? (row as CreateJournalEntryDto).mutationId,
                            }),
                          ),
                        ),
                      )
                      .then(() => {
                        setMutations((prevMutations) =>
                          prevMutations?.map((m) =>
                            m.mutationId === mutationId
                              ? { ...m, journalEntryCount: journalEntryOrArray.length }
                              : m,
                          ) ?? prevMutations,
                        );
                        setTransactionModalOpen(false);
                        setSelectedExistingJournalEntry(null);
                        setSelectedExistingJournalEntries(null);
                        setSelectedMutation(null);
                        toast.success(
                          journalEntryOrArray.length === 1 ? 'Journal entry updated' : 'Split journal entries saved',
                        );
                      })
                      .catch(() => toast.error('Failed to save split journal entries'));
                    return;
                  }

                  // Create flow: single "split" = create directly
                  if (journalEntryOrArray.length === 1) {
                    const single = journalEntryOrArray[0] as CreateJournalEntryDto;
                    bankAccountService.createJournalEntry({
                      ...single,
                      dashboardId: dashboardId!,
                      mutationId: mutationId ?? single.mutationId,
                    }).then(() => {
                      setMutations((prevMutations) => {
                        return (
                          prevMutations?.map((m) =>
                            m.mutationId === single.mutationId
                              ? { ...m, journalEntryCount: m.journalEntryCount + 1 }
                              : m,
                          ) ?? prevMutations
                        );
                      });
                      setTransactionModalOpen(false);
                      setSelectedMutation(null);
                      toast.success(t('success.importBankTransactions'));
                    }).catch(() => toast.error('Failed to create journal entry'));
                    return;
                  }

                  // Create flow - pass multiple journal entries to AssignSplitTransactionModal
                  setTransactionModalOpen(false);
                  setSelectedMutation(null);
                  setSplitModalCreatedIds([]);
                  setSplitModalTransactionsOpen(true);
                  setSplitModalJournalEntries(journalEntryOrArray.map(e => e as CreateJournalEntryDto));
                  setSplitModalTotalCount(journalEntryOrArray.length);
                  return;
                }

                // Single journal entry
                if (selectedExistingJournalEntry) {
                  // Edit flow: update existing journal entry
                  bankAccountService
                    .updateJournalEntry(selectedExistingJournalEntry.journalEntryId, journalEntryOrArray)
                    .then(() => {
                      setTransactionModalOpen(false);
                      setSelectedExistingJournalEntry(null);
                      setSelectedExistingJournalEntries(null);
                      setSelectedMutation(null);
                      toast.success('Journal entry updated');
                    })
                    .catch(() => toast.error('Failed to update journal entry'));
                  return;
                }

                // Create flow: create new journal entry
                const mutationId = selectedMutation?.mutationId;
                bankAccountService.createJournalEntry({
                  ...journalEntryOrArray,
                  dashboardId: dashboardId!,
                  mutationId: mutationId ?? (journalEntryOrArray as CreateJournalEntryDto).mutationId,
                }).then(() => {
                  setMutations((prevMutations) => {
                    return (
                      prevMutations?.map((m) =>
                        m.mutationId === journalEntryOrArray.mutationId
                          ? {
                            ...m,
                            journalEntryCount: m.journalEntryCount + 1,
                          }
                          : m,
                      ) ?? prevMutations
                    );
                  });
                  setTransactionModalOpen(false);
                  setSelectedMutation(null);
                  toast.success('Journal entry created');
                }).catch(() => toast.error('Failed to create journal entry'));
              }}
            />
          )}

        {splitModalMutation && (
          <MutationIntoTransactionsModal
            open={splitModalMutationOpen}
            setOpen={(open) => {
              if (!open) {
                setSplitModalMutationOpen(false);
                setTimeout(() => setSplitModalMutation(null), 200);
              }
            }}
            mutation={splitModalMutation}
            onTransactionSplit={(tns) => {
              setSplitModalMutationOpen(false);
              setTimeout(() => setSplitModalMutation(null), 200);
              setSplitModalCreatedIds([]);
              setSplitModalTransactionsOpen(true);
              // Note: MutationIntoTransactionsModal still returns CreateBankTransactionModel[]
              // This will need to be converted to CreateJournalEntryDto[] when that modal is refactored
              // For now, we'll need to convert them here or update the modal
              setSplitModalJournalEntries(null); // TODO: Convert tns to journal entries
              setSplitModalTotalCount(tns.length);
            }}
          />
        )}
        {splitModalJournalEntries && splitModalJournalEntries.length > 0 && (
          <AssignSplitTransactionModal
            open={splitModalTransactionsOpen}
            setOpen={(open: boolean) => {
              if (!open && splitModalCreatedIds.length > 0) {
                // Cancel: rollback all journal entries created in this batch
                Promise.all(
                  splitModalCreatedIds.map(({ journalEntryId }) =>
                    bankAccountService.deleteJournalEntry(journalEntryId),
                  ),
                )
                  .then(() => {
                    const mutationId =
                      splitModalCreatedIds[0]?.mutationId ??
                      splitModalMutation?.mutationId ??
                      selectedMutation?.mutationId;
                    if (mutationId !== undefined) {
                      setMutations((prevMutations) =>
                        prevMutations?.map((m) =>
                          m.mutationId === mutationId
                            ? {
                              ...m,
                              journalEntryCount: Math.max(
                                0,
                                m.journalEntryCount - splitModalCreatedIds.length,
                              ),
                            }
                            : m,
                        ) ?? prevMutations,
                      );
                    }
                    setSplitModalCreatedIds([]);
                    setSplitModalJournalEntries(null);
                    setSplitModalTransactionsOpen(false);
                    setSplitModalMutation(null);
                    setSplitModalTotalCount(0);
                    toast.info(t('cancelRollback') ?? 'Assignments cancelled');
                  })
                  .catch((err) => {
                    console.error('Rollback failed:', err);
                    toast.error('Failed to cancel assignments');
                  });
              } else {
                setSplitModalTransactionsOpen(open);
                if (!open) {
                  setSplitModalJournalEntries(null);
                  setSplitModalMutation(null);
                  setSplitModalTotalCount(0);
                  setSplitModalCreatedIds([]);
                }
              }
            }}
            // Note: AssignSplitTransactionModal still expects CreateBankTransactionModel
            // This will need to be refactored separately
            transaction={splitModalJournalEntries?.[0] as any}
            mutation={splitModalMutation || selectedMutation || undefined}
            currentIndex={splitModalTotalCount - (splitModalJournalEntries?.length ?? 0) + 1}
            totalCount={splitModalTotalCount}
            onAssignSuccess={(tns: CreateBankTransactionModel) => {
              bankAccountService.createTransactionWithParticipations(tns).then(() => {
                setSplitModalJournalEntries((prevEntries) => (prevEntries ? prevEntries.slice(1) : null));
                setMutations((prevMutations) => {
                  return (
                    prevMutations?.map((m) =>
                      m.mutationId === tns.mutationId
                        ? {
                            ...m,
                            journalEntryCount: m.journalEntryCount + 1,
                          }
                        : m,
                    ) ?? prevMutations
                  );
                });
              });
            }}
          />
        )}
      </div>
    </>
  );
}


function LinkBankAccountModal({
  open,
  setOpen,
  onBankChosen,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
  onBankChosen: (bankId: string, fromDate?: string) => Promise<void>;
}): JSX.Element {
  const { t: tLink } = useTranslation('', {
    keyPrefix: 'page.financial.accountEntry.linkBankModal',
  });
  const [institutions, setInstitutions] = useState<GoCardlessInstitution[]>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedBank, setSelectedBank] = useState<GoCardlessInstitution | null>(null);
  const [startDate, setStartDate] = useState<Date>();

  useEffect(() => {
    if (open && !institutions) {
      bankAccountService.getDutchBanks().then((banks) => {
        setInstitutions(banks);
      });
    }
  }, [open, institutions]);

  const handleBankChosen = (instId: string, fromDate?: string) => {
    setIsLoading(true);
    setTimeout(() => {
      onBankChosen(instId, fromDate).finally(() => setIsLoading(false));
    });
  };

  return (
    <ModalLayout
      open={open}
      setOpen={setOpen}
      closeButton
      className="z-50 mx-6 my-8 w-full rounded-[12px] bg-secondary-50 p-8 sm:max-w-[700px]">
      <div>
        <h2 className="mb-4 text-lg font-semibold">{tLink('selectBank')}</h2>
        <div className="flex max-h-[calc(100vh-20rem)] flex-col gap-2 overflow-y-auto">
          {institutions && !isLoading ? (
            <>
              {institutions.map((inst) => (
                <button
                  key={inst.id}
                  className={`mb-2 flex w-full items-center rounded-md border-2 border-secondary-200 ${inst.id === selectedBank?.id ? 'bg-secondary-200' : 'bg-secondary-50'
                    } p-4 hover:bg-secondary-200`}
                  type="button"
                  onClick={() => setSelectedBank(inst)}>
                  <div className="flex items-center gap-2">
                    <img
                      src={inst.logo}
                      alt={`${inst.name} logo`}
                      className="h-8 w-8 object-contain"
                    />
                    <p>{inst.name}</p>
                  </div>
                </button>
              ))}
              {selectedBank && (
                <Labeled label={tLink('startDay')}>
                  <DatePicker
                    date={startDate}
                    setDate={setStartDate}
                    minDate={dayjs().subtract(
                      selectedBank.transaction_total_days
                        ? +selectedBank.transaction_total_days
                        : 90,
                      'days',
                    )}
                    maxDate={dayjs()}
                    includeTime={false}
                  />
                  <p className="text-sm text-gray-500">{tLink('startDayHelper')}</p>
                </Labeled>
              )}
              <Button
                variant="primary-dark"
                className="mt-4 self-end"
                disabled={!selectedBank}
                onClick={() =>
                  handleBankChosen(
                    selectedBank!.id,
                    startDate ? dayjs(startDate).format('YYYY-MM-DD') : undefined,
                  )
                }>
                {tLink('linkButton')}
              </Button>
            </>
          ) : (
            <CgSpinner size={40} className="animate-spin text-primary-300" />
          )}
        </div>
      </div>
    </ModalLayout>
  );
}

function AssignBulkMutationModal({
  open,
  setOpen,
  unassignedMutations,
  onParseSuccess,
  setParentConfirmedMatches,
  onUpdateMutations,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
  unassignedMutations: BankMutation[];
  onParseSuccess: (
    confirmedMatches: {
      transaction: CreateBankTransactionModel;
      matchedConnection: Connection;
      contributionPlanId?: number;
    }[],
    selectedGroupMutations: CreateBankTransactionModel[],
  ) => void;
  setParentConfirmedMatches: React.Dispatch<
    React.SetStateAction<
      | {
        transaction: CreateBankTransactionModel;
        matchedConnection: Connection;
        contributionPlanId?: number;
      }[]
      | null
    >
  >;
  onUpdateMutations: React.Dispatch<React.SetStateAction<BankMutation[] | null | undefined>>;
}): JSX.Element {
  const { t } = useTranslation();
  const [step, setStep] = React.useState(0);
  const [matchedTransactions, setMatchedTransactions] = React.useState<Array<{
    mutation: CreateBankTransactionModel;
    matchedConnection?: Connection;
  }> | null>(null);

  const [internalMutationMatches, setInternalMutationMatches] = React.useState<
    | {
      mutation: CreateBankTransactionModel;
      counterpartMutation: BankMutation;
      confirmed: boolean;
    }[]
    | null
  >(null);

  const [internalConfirmedMatches, setInternalConfirmedMatches] = React.useState<
    | {
      transaction: CreateBankTransactionModel;
      matchedConnection: Connection;
      contributionPlanId?: number;
    }[]
    | null
  >(null);

  const [eventExpenseMatches, setEventExpenseMatches] = React.useState<
    | {
      transaction: CreateBankTransactionModel;
      matchedEvent: Event;
    }[]
    | null
  >(null);

  const connectionsAll = useSelector((state: RootState) => state.connections);
  const connections = connectionsAll.filter((c) => c.type === ConnectionType.MEMBER);

  useEffect(() => {
    if (open) {
      const matched = matchMutationsToConnections(unassignedMutations, connections);
      setMatchedTransactions(
        matched.map(({ transaction, matchedConnection }) => ({
          mutation: transaction,
          matchedConnection,
        })),
      );
    }
  }, [open]);

  // const handleFileUpload = (files: FileList) => {
  //   if (files.length === 1) {
  //     const file = files[0];
  //     new RabobankParser(accountNumber).parse(file).then((parsed) => {
  //       const matched = [] as {
  //         mutation: CreateBankTransactionModel;
  //         matchedConnection?: Connection | undefined;
  //       }[]; // matchTransactionsToConnections(parsed, connections);
  //       setMatchedTransactions(matched);
  //       setTransactions(parsed);
  //       setStep(1);
  //     });
  //   } else {
  //     toast.error(t('toast.error.oneFile'));
  //   }
  // };

  const handleFinish = async (selectedGroupMutations: CreateBankTransactionModel[]) => {
    // Process internal mutation matches first
    if (internalMutationMatches && internalMutationMatches.length > 0) {
      try {
        const confirmedInternalMatches = internalMutationMatches.filter((match) => match.confirmed);

        if (confirmedInternalMatches.length > 0) {
          // Create internal transfers for confirmed matches
          await Promise.all(
            confirmedInternalMatches.map((match) =>
              bankAccountService.createInternalTransferFromMutations(
                match.mutation.mutationId!,
                match.counterpartMutation.mutationId,
                match.mutation.description,
              ),
            ),
          );

          // Update mutations state to mark them as processed
          onUpdateMutations((prevMutations) => {
            if (!prevMutations) return prevMutations;
            return prevMutations.map((mutation) => {
              const wasProcessed = confirmedInternalMatches.some(
                (match) =>
                  match.mutation.mutationId === mutation.mutationId ||
                  match.counterpartMutation.mutationId === mutation.mutationId,
              );
              return wasProcessed
                ? { ...mutation, journalEntryCount: mutation.journalEntryCount + 1 }
                : mutation;
            });
          });
        }
      } catch (error) {
        console.error('Error creating internal transfers:', error);
        // Continue with the flow even if internal transfer creation fails
      }
    }

    // Process event expense matches
    if (eventExpenseMatches && eventExpenseMatches.length > 0) {
      try {
        const personalExpenseTransactions: CreateBankTransactionModel[] = eventExpenseMatches
          .map((eventMatch) => {
            const { transaction, matchedEvent } = eventMatch;

            // Get participants who attended the event
            const attendingParticipants = matchedEvent.participants.filter(
              (participant) => participant.answer === EventParticipationAnswer.YES,
            );

            if (attendingParticipants.length > 0) {
              // Find connections for the attending participants
              const attendingConnections = attendingParticipants
                .map((participant) => connections.find((conn) => conn.memberId === participant.id))
                .filter((conn): conn is Connection => conn !== undefined);

              // Edge case: Only create transaction if there are attending connections
              if (attendingConnections.length > 0) {
                // Calculate equal split with proper rounding to ensure total equals original amount
                const totalAmount = Math.abs(transaction.amount);
                const baseAmount = Math.floor(totalAmount / attendingConnections.length);
                const remainder = totalAmount - baseAmount * attendingConnections.length;

                // Create participations with proper rounding
                const participations = attendingConnections.map((connection, index) => {
                  // Distribute remainder cents to first N participants
                  const amount = baseAmount + (index < remainder ? 1 : 0);
                  return {
                    dashboardMemberId: +connection.dashboardMemberId,
                    amount: transaction.amount < 0 ? -amount : amount, // Preserve original sign
                  };
                });

                // Create a single personal expense transaction with multiple participations
                return {
                  ...transaction,
                  transactionType: TransactionType.PERSONAL_EXPENSE,
                  amount: transaction.amount, // Keep original amount
                  participations,
                } as CreateBankTransactionModel;
              }
            }
            return null;
          })
          .filter((transaction): transaction is CreateBankTransactionModel => transaction !== null);

        // Create all transactions at once
        if (personalExpenseTransactions.length > 0) {
          await Promise.all(
            personalExpenseTransactions.map((tns) =>
              bankAccountService.createTransactionWithParticipations(tns),
            ),
          );

          // Update mutations state to mark them as processed
          onUpdateMutations((prevMutations) => {
            if (!prevMutations) return prevMutations;
            return prevMutations.map((mutation) => {
              const wasProcessed = personalExpenseTransactions.some(
                (tns) => tns.mutationId === mutation.mutationId,
              );
              return wasProcessed
                ? { ...mutation, journalEntryCount: mutation.journalEntryCount + 1 }
                : mutation;
            });
          });
        }
      } catch (error) {
        console.error('Error creating event expense transactions:', error);
        // Continue with the flow even if event expense creation fails
      }
    }

    // Filter out mutations that were already processed as internal transfers or event expenses
    const filteredSelectedGroupMutations = selectedGroupMutations.filter((mutation) => {
      const wasInternalTransfer = internalMutationMatches?.some(
        (internalMatch) =>
          internalMatch.mutation.mutationId === mutation.mutationId ||
          internalMatch.counterpartMutation.mutationId === mutation.mutationId,
      );
      const wasEventExpense = eventExpenseMatches?.some(
        (eventMatch) => eventMatch.transaction.mutationId === mutation.mutationId,
      );
      return !wasInternalTransfer && !wasEventExpense;
    });

    onParseSuccess(internalConfirmedMatches ?? [], filteredSelectedGroupMutations);
    setOpen(false); // close modal
    setStep(0); // reset step
  };

  const renderStepContent = () => {
    switch (step) {
      case 0:
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-primary-900">
              {t('component.modal.uploadFinancialFile.internalMutationMatchTitle')}
            </h3>
            <p className="text-primary-800 text-sm">
              {t('component.modal.uploadFinancialFile.internalMutationMatchSubtitle')}
            </p>
            <VerifyInternalMutationMatchesView
              unassignedMutations={unassignedMutations}
              onConfirm={(confirmedMatches) => {
                setInternalMutationMatches(confirmedMatches);
                setStep(1);
              }}
            />
          </div>
        );
      case 1:
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-primary-900">
              {t('component.modal.uploadFinancialFile.reviewTitle')}
            </h3>
            {/* You could display a preview or offer categorization or tagging here */}
            <p className="text-primary-800 text-sm">
              {t('component.modal.uploadFinancialFile.reviewSubtitle')}
            </p>
            <VerifyTransactionMatchesView
              matches={matchedTransactions?.filter((m) => m.matchedConnection)!}
              onConfirm={(confirmed) => {
                setInternalConfirmedMatches(confirmed);
                setParentConfirmedMatches(confirmed);
                setMatchedTransactions(
                  (prev) =>
                    prev?.map((m) => {
                      const found = confirmed.find((c) => c.transaction === m.mutation);
                      return found
                        ? { ...m, matchedConnection: found.matchedConnection }
                        : { ...m, matchedConnection: undefined };
                    }) ?? null,
                );
                // Check if we need to show the contribution plan matching step
                // For now, we'll always show it, but we could add logic here to check
                // if there are any contribution plans that could be matched
                setStep(2);
              }}
            />
          </div>
        );
      case 2:
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-primary-900">
              {t('component.modal.uploadFinancialFile.contributionPlanMatchTitle')}
            </h3>
            <p className="text-primary-800 text-sm">
              {t('component.modal.uploadFinancialFile.contributionPlanMatchSubtitle')}
            </p>
            <VerifyContributionPlanMatchesView
              matches={
                internalConfirmedMatches?.map(
                  (match: {
                    transaction: CreateBankTransactionModel;
                    matchedConnection: Connection;
                    contributionPlanId?: number;
                  }) => ({
                    transaction: match.transaction,
                    matchedConnection: match.matchedConnection,
                  }),
                ) ?? []
              }
              onConfirm={(contributionPlanMatches) => {
                // Update confirmed matches with contribution plan IDs
                const updatedMatches =
                  internalConfirmedMatches?.map(
                    (match: {
                      transaction: CreateBankTransactionModel;
                      matchedConnection: Connection;
                      contributionPlanId?: number;
                    }) => {
                      const planMatch = contributionPlanMatches.find(
                        (pm) =>
                          pm.transaction === match.transaction &&
                          pm.matchedConnection.dashboardMemberId === match.matchedConnection.dashboardMemberId,
                      );
                      return {
                        ...match,
                        contributionPlanId: planMatch?.contributionPlanId,
                      };
                    },
                  ) ?? [];
                setInternalConfirmedMatches(updatedMatches);
                setParentConfirmedMatches(updatedMatches);
                setStep(3);
              }}
            />
          </div>
        );
      case 3:
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-primary-900">
              {t('component.modal.uploadFinancialFile.eventExpenseMatchTitle')}
            </h3>
            <p className="text-primary-800 text-sm">
              {t('component.modal.uploadFinancialFile.eventExpenseMatchSubtitle')}
            </p>
            <VerifyEventExpenseMatchesView
              unmatched={
                matchedTransactions
                  ?.filter((m) => !m.matchedConnection)
                  .map((m) => m.mutation)
                  .filter(
                    (mutation) =>
                      !internalMutationMatches?.some(
                        (internalMatch) =>
                          internalMatch.mutation.mutationId === mutation.mutationId ||
                          internalMatch.counterpartMutation.mutationId === mutation.mutationId,
                      ),
                  ) ?? []
              }
              onConfirm={(eventMatches) => {
                setEventExpenseMatches(eventMatches);
                setStep(4);
              }}
            />
          </div>
        );
      case 4:
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-primary-900">
              {t('component.modal.uploadFinancialFile.finalStepTitle')}
            </h3>
            <p className="text-primary-800 text-sm">
              {t('component.modal.uploadFinancialFile.finalStepSubtitle')}
            </p>
            <HandleUnmatchedTransactionsView
              unmatched={
                matchedTransactions
                  ?.filter((m) => !m.matchedConnection)
                  .map((m) => m.mutation)
                  .filter((mutation) => {
                    const wasInternalTransfer = internalMutationMatches?.some(
                      (internalMatch) =>
                        internalMatch.mutation.mutationId === mutation.mutationId ||
                        internalMatch.counterpartMutation.mutationId === mutation.mutationId,
                    );
                    const wasEventExpense = eventExpenseMatches?.some(
                      (eventMatch) => eventMatch.transaction.mutationId === mutation.mutationId,
                    );
                    return !wasInternalTransfer && !wasEventExpense;
                  }) ?? []
              }
              onConfirm={(selected) => {
                handleFinish(selected);
              }}
            />
          </div>
        );
      default:
        console.warn(`Unknown step: ${step}`);
        return null;
    }
  };

  return (
    <ModalLayout
      open={open}
      setOpen={setOpen}
      closeButton
      className="z-50 mx-6 my-8 w-full rounded-[12px] bg-secondary-50 p-8 sm:max-w-[700px]">
      {renderStepContent()}
    </ModalLayout>
  );
}
